import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { rem } from 'polished';
import { FlexRow, FlexRowCenterVertical } from '../../Defaults/Flexbox/Flexbox';
import { __ } from 'react-i18n';
import { formatPrice, TextP, ZemplinLoaderWrapper } from '../../..';
import { TableWrapper } from '../General/TableWrapper';
import { TableRow, SecondTableTh } from '../General/Table';
import { InvoiceRow } from './InvoiceRow';
import { Select } from '../../Zemplin/General/Select';
import { Input } from '../../Zemplin/General/Input';
import * as moment from 'moment';

interface Props {
  orders: any;
  isFetching: boolean;
  downloadFile: any;
  currency: string;
  refetchData?: any;
  sumPrice?: number;
}

function Invoices({
  orders,
  isFetching,
  downloadFile,
  currency,
  refetchData,
  sumPrice,
}: Props) {
  const date = new Date();
  date.setDate(date.getDate());
  const firstDate = new Date(date.getFullYear(), 1, 1);
  firstDate.setDate(firstDate.getDate());
  const today = moment();
  const [fromDate, setFromDate] = React.useState(
    today.subtract('3', 'months').format('YYYY-MM-DD'),
  );
  const [fromFormatted, setFromFormatted] = React.useState(null);
  const [toFormatted, setToFormatted] = React.useState(null);

  const [toDate, setToDate] = React.useState(moment().format('YYYY-MM-DD'));
  const [selectState, setSelectState] = React.useState('ALL');
  const [paidSelectState, setPaidSelectState] = React.useState('ALL');
  const columns = [
    { name: __('Doklad') },
    { name: __('Č. dokladu') },
    { name: __('Dátum') },
    { name: __('Dátum splatnosti') },
    { name: __('Stav') },
    { name: __('Stiahnuť'), align: 'center' },
    { name: __('Suma'), align: 'right' },
  ];
  const footerColumns = [
    { name: __('Spolu') },
    { name: '' },
    { name: '' },
    { name: '' },
    { name: '' },
    { name: '' },
    {
      name: formatPrice(sumPrice || 0, currency),
      align: 'right',
    },
  ];
  const headers =
    columns &&
    columns.map((item: any) => (
      <SecondTableTh
        width={item.width}
        minWidth={item.minWidth}
        align={item.align || 'left'}
        key={item.name}
      >
        {item.name}
      </SecondTableTh>
    ));

  const footers =
    footerColumns &&
    footerColumns.map((item: any) => (
      <SecondTableTh
        width={item.width}
        minWidth={item.minWidth}
        align={item.align || 'left'}
        key={item.name}
      >
        {item.name}
      </SecondTableTh>
    ));

  const orderRows =
    orders &&
    orders.map(o => {
      return (
        <InvoiceRow
          order={o}
          key={o.helios_dokument_id}
          status={!!o.paid}
          downloadFile={downloadFile}
          currency={currency}
        />
      );
    });

  const onChangeSelect = e => {
    const {
      target: { value },
    } = e;
    setSelectState(value);
    refetchData({
      fromDate,
      toDate,
      paid: paidSelectState,
      type: value,
    });
  };

  const onChangePaidSelect = e => {
    const {
      target: { value },
    } = e;
    setPaidSelectState(value);

    refetchData({ fromDate, toDate, type: selectState, paid: value });
  };

  const changeFromDate = e => {
    const value = e.target.value;
    setFromDate(value);
    let formatted: any = new Date(value);
    const dd = String(formatted.getDate()).padStart(2, '0');
    const mm = String(formatted.getMonth() + 1).padStart(2, '0'); // January is 0!
    const yyyy = formatted.getFullYear();
    formatted = `${dd}-${mm}-${yyyy}`;
    setFromFormatted(formatted);
    refetchData({
      fromDate: formatted,
      toDate: toFormatted || undefined,
      type: selectState,
      paid: paidSelectState,
    });
  };

  const changeToDate = e => {
    const value = e.target.value;
    setToDate(value);
    let formatted: any = new Date(value);
    const dd = String(formatted.getDate()).padStart(2, '0');
    const mm = String(formatted.getMonth() + 1).padStart(2, '0'); // January is 0!
    const yyyy = formatted.getFullYear();
    formatted = `${dd}-${mm}-${yyyy}`;
    setToFormatted(formatted);
    refetchData({
      fromDate: fromFormatted || undefined,
      toDate: formatted,
      type: selectState,
      paid: paidSelectState,
    });
  };

  return (
    <Wrapper>
      <Filters>
        <TypeSelectorsWrapper>
          <Select
            value={selectState}
            options={[
              { name: __('Faktúry a dobropisy'), value: 'ALL' },
              { name: __('Faktúry'), value: 'FAKTURA' },
              { name: __('Dobropisy'), value: 'DOBROPIS' },
            ]}
            onChange={onChangeSelect}
            maxWidth={240}
            marginRight={24}
            mobileMarginRight={0}
            tabletMarginRight={32}
            tabletMarginBottom={0}
            mobileMarginBottom={24}
          />
          <Select
            value={paidSelectState}
            options={[
              { name: __('Uhradené a neuhradené'), value: 'ALL' },
              { name: __('Uhradené'), value: 'PAID' },
              { name: __('Neuhradené'), value: 'NOTPAID' },
            ]}
            onChange={onChangePaidSelect}
            maxWidth={240}
            marginRight={14}
            mobileMarginBottom={0}
          />
        </TypeSelectorsWrapper>

        <DateSelectorsWrapper>
          <Label>{`${__('od')}`}</Label>
          <Input
            min={'2021-07-01'}
            type="date"
            name={'from'}
            value={fromDate}
            onChange={changeFromDate}
            maxWidth={160}
            marginBottom={24}
            maxWidthMobile={160}
          />
          <Label>{`${__('do')}`}</Label>
          <Input
            type="date"
            name={'to'}
            value={toDate}
            onChange={changeToDate}
            maxWidth={160}
            maxWidthMobile={160}
          />
        </DateSelectorsWrapper>
      </Filters>
      {isFetching ? (
        <ZemplinLoaderWrapper height={500} />
      ) : (
        <TableWrapper
          tableHasBorder={true}
          tableHasBoxShadow={true}
          noItemsText={__('Nenašli sa žiadne faktúry ani dobropisy')}
          headers={headers}
          rows={orderRows}
        />
      )}
    </Wrapper>
  );
}

const animateIn = keyframes`
   0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }
  
  100% {
    opacity: 1;
  }
`;

const Wrapper = styled(FlexRow)`
  flex-flow: row wrap;
  margin-bottom: ${rem(24)};
  animation: ${animateIn} 0.2s ease;
  width: 100%;

  ${({ theme }) => theme.mediab.m810`
      margin: 0;
      margin-bottom: ${({ visible }) => (visible ? rem(32) : 0)};
      margin-right: -${rem(16)};
  `}e
`;

const TypeSelectorsWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-flow: row;

  @media only screen and (max-width: 810px) {
    margin-bottom: ${rem(24)};
    flex-wrap: wrap;
  }
`;

const DateSelectorsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row;
  align-items: center;
`;

export const Filters = styled(FlexRowCenterVertical)`
  margin-bottom: ${rem(32)};
  width: 100%;
  align-items: center;

  ${({ theme }) => theme.mediab.m810`
      flex-wrap: wrap;
  `};
`;

export const Label = styled.label`
  font-size: ${rem(16)};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 ${rem(16)};

  ${({ theme }) => theme.mediab.m810`
       margin: ${rem(16)} ${rem(16)} ${rem(8)};
  `};
`;

export { Invoices };
